import { z } from "zod";

export const jobTypeEnum = z.enum([
  "ingest-document",
  "ingest-web",
  "embed",
  "classify",
  "recrawl",
  "recrawl-scheduler",
]);
export type JobType = z.infer<typeof jobTypeEnum>;

export const jobPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  sourceId: z.string().uuid(),
  sourceVersionId: z.string().uuid(),
  jobType: jobTypeEnum,
});

export type JobPayload = z.infer<typeof jobPayloadSchema>;

export const webCrawlConfigSchema = z.object({
  maxDepth: z.number().int().min(0).max(5).default(0),
  maxPages: z.number().int().min(1).max(500).default(50),
  /** Explicit list of URLs to fetch — when set, depth-based crawling is skipped. */
  pages: z.array(z.string().url()).max(100).optional(),
  includePaths: z.array(z.string()).optional(),
  excludePaths: z.array(z.string()).optional(),
  jsCode: z.array(z.string().max(2000)).max(10).optional(),
  waitForSelector: z.string().max(200).optional(),
});

export type WebCrawlConfig = z.infer<typeof webCrawlConfigSchema>;

export const ingestWebPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  sourceId: z.string().uuid(),
  sourceVersionId: z.string().uuid().optional(),
  url: z.string().url(),
  crawlConfig: webCrawlConfigSchema,
  recrawl: z.boolean().default(false),
  autoClassify: z.boolean().default(false),
  sessionId: z.string().uuid().optional(),
});

export type IngestWebPayload = z.infer<typeof ingestWebPayloadSchema>;

export const suggestSourcesPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  scenarioId: z.string().uuid(),
  searchLimit: z.number().int().min(5).max(50).default(10),
});

export type SuggestSourcesPayload = z.infer<typeof suggestSourcesPayloadSchema>;

export const validateScenarioPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  scenarioId: z.string().uuid(),
  trigger: z.enum(["manual", "source_update", "scheduled"]).optional(),
});

export type ValidateScenarioPayload = z.infer<
  typeof validateScenarioPayloadSchema
>;

export const runSpecIntegrityChecksPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  specVersionId: z.string().uuid(),
});

export type RunSpecIntegrityChecksPayload = z.infer<
  typeof runSpecIntegrityChecksPayloadSchema
>;

export const runLiveWalkthroughPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  walkthroughId: z.string().uuid(),
  projectId: z.string().uuid(),
  targetUrl: z.string().url(),
  maxSteps: z.number().int().min(3).max(20).default(15),
  enabledLenses: z.array(z.string()).default(["persona"]),
  /** Pre-assembled domain knowledge text for domain_expert lens */
  knowledgeText: z.string().optional(),
  /** Pre-assembled vocabulary text for domain_expert lens */
  vocabularyText: z.string().optional(),
});

export type RunLiveWalkthroughPayload = z.infer<
  typeof runLiveWalkthroughPayloadSchema
>;

export const generatePdfReportPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  walkthroughId: z.string().uuid(),
});

export type GeneratePdfReportPayload = z.infer<
  typeof generatePdfReportPayloadSchema
>;
